import type { ComponentConfig, ComponentManager } from './types'

export class BaseComponent implements ComponentManager {
  protected container: HTMLElement
  protected templatePath: string

  constructor(config: ComponentConfig) {
    const container = document.getElementById(config.container)
    if (!container) {
      throw new Error(`Container with id "${config.container}" not found`)
    }
    this.container = container
    this.templatePath = config.template
  }

  async loadHTML(): Promise<void> {
    try {
      const response = await fetch(this.templatePath)
      const html = await response.text()
      this.container.innerHTML = html
    } catch (error) {
      console.error(`Error loading HTML from ${this.templatePath}:`, error)
    }
  }

  bindEvents(): void {
    // 子類別覆寫此方法
  }
}

// 導航列元件
export class NavbarComponent extends BaseComponent {
  constructor() {
    super({ container: 'navbar-container', template: '/components/navbar.html' })
  }

  bindEvents(): void {
    // 設置導航點擊事件
    this.setupNavigationEvents()
  }

  // 設置導航點擊事件
  private setupNavigationEvents(): void {
    const navLinks = document.querySelectorAll('.nav-link')
    
    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href')
        if (href && href.startsWith('#')) {
          e.preventDefault()
          const targetId = href.substring(1)
          
          // 取得當前頁面類型
          const currentPage = document.documentElement.dataset.page || 'home'
          
          // 特殊處理：回到首頁
          if (targetId === 'top') {
            if (currentPage === 'home') {
              // 在首頁：滾動到頁頂
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              })
            } else {
              // 在其他頁：導航到首頁
              window.location.href = '/'
            }
          } else {
            // 其他錨點導航
            if (currentPage === 'home') {
              // 在首頁：滾動到對應區塊
              this.scrollToSection(targetId)
            } else {
              // 在其他頁：導航到首頁，然後滾動到錨點
              window.location.href = '/#' + targetId
            }
          }
        }
      })
    })
  }

  // 滾動到指定區塊
  private scrollToSection(sectionId: string): void {
    const targetElement = document.getElementById(sectionId)
    
    if (targetElement) {
      const offset = 80 // 適當的導航列偏移量
      const targetPosition = targetElement.offsetTop - offset
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      })
    }
  }

  setActivePage(pageName: string): void {
    const navLinks = document.querySelectorAll('.nav-link')
    navLinks.forEach(link => {
      link.classList.remove('active')
      
      const href = link.getAttribute('href')
      if (
        (pageName === 'home' && href === '#') ||
        (pageName === 'about' && href === '#about') ||
        (pageName === 'services' && href === '#services') ||
        (pageName === 'contact' && href === '#contact')
      ) {
        link.classList.add('active')
      }
    })
  }
}

// 頁尾元件
export class FooterComponent extends BaseComponent {
  constructor() {
    super({ container: 'footer-container', template: '/components/footer.html' })
  }

  async loadHTML(): Promise<void> {
    try {
      const response = await fetch('/components/footer.html')
      const html = await response.text()
      
      const footerContainer = document.createElement('div')
      footerContainer.innerHTML = html
      
      const app = document.getElementById('app')
      if (app && footerContainer.firstElementChild) {
        app.appendChild(footerContainer.firstElementChild)
      }
    } catch (error) {
      console.error('載入頁尾失敗:', error)
    }
  }
}

// 主橫幅元件
export class HeroComponent extends BaseComponent {
  constructor() {
    super({ container: 'hero-container', template: '/components/hero.html' })
  }

  bindEvents(): void {
    const startBtn = this.container.querySelector('#startBtn') as HTMLButtonElement
    if (startBtn) {
      startBtn.addEventListener('click', () => {
        console.log('開始使用按鈕被點擊')
        alert('歡迎開始使用 LeadWin！')
      })
    }
  }
}

// 專業服務元件
export class ServicesComponent extends BaseComponent {
  constructor() {
    super({ container: 'services-container', template: '/components/services.html' })
  }

  bindEvents(): void {
    // 可以添加專業服務頁面的互動功能
    console.log('Services section loaded')
  }
}

// 聯絡我們元件
export class ContactComponent extends BaseComponent {
  constructor() {
    super({ container: 'contact-container', template: '/components/contact.html' })
  }

  bindEvents(): void {
    // 聯絡表單提交處理
    const contactForm = this.container.querySelector('#contactForm') as HTMLFormElement
    if (contactForm) {
      contactForm.addEventListener('submit', async (e) => {
        e.preventDefault()
        
        const formData = new FormData(contactForm)
        const name = formData.get('name') as string
        const email = formData.get('email') as string
        const message = formData.get('message') as string
        
        // 禁用送出按鈕，防止重複提交
        const submitBtn = contactForm.querySelector('button[type="submit"]') as HTMLButtonElement
        const originalText = submitBtn.textContent
        submitBtn.disabled = true
        
        // 添加 spinner
        submitBtn.innerHTML = `
          <span class="spinner-border spinner-border-sm me-2" role="status"></span>
          送出中...
        `
        
        try {
          // 模擬 API 調用延遲 (3-5秒)
          await this.simulateAPICall({ name, email, message })
          
          // 顯示成功 toast
          this.showToast('success', '我們已收到您的訊息，請等候回復')
          
          // 清空表單
          contactForm.reset()
          
        } catch (error) {
          // 顯示錯誤 toast
          this.showToast('error', '送出失敗，請稍後再試')
          console.error('Contact form submission error:', error)
        } finally {
          // 恢復按鈕狀態
          submitBtn.disabled = false
          submitBtn.textContent = originalText
        }
      })
    }
  }

  // 顯示 Bootstrap Toast 通知
  private showToast(type: 'success' | 'error', message: string): void {
    // 創建 toast 容器
    const toastContainer = document.getElementById('toast-container') || this.createToastContainer()
    
    // 創建 toast 元素
    const toastId = `toast-${Date.now()}`
    const toastHtml = `
      <div id="${toastId}" class="toast align-items-center text-white bg-${type === 'success' ? 'success' : 'danger'} border-0" role="alert">
        <div class="d-flex">
          <div class="toast-body">
            ${message}
          </div>
          <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"></button>
        </div>
      </div>
    `
    
    // 添加到容器
    toastContainer.insertAdjacentHTML('beforeend', toastHtml)
    
    // 初始化 Bootstrap toast
    const toastElement = document.getElementById(toastId) as HTMLElement
    const toast = new (window as any).bootstrap.Toast(toastElement, {
      autohide: true,
      delay: 5000 // 5秒後自動消失
    })
    
    // 顯示 toast
    toast.show()
    
    // 監聽隱藏事件，移除元素
    toastElement.addEventListener('hidden.bs.toast', () => {
      toastElement.remove()
    })
  }

  // 創建 toast 容器
  private createToastContainer(): HTMLElement {
    const container = document.createElement('div')
    container.id = 'toast-container'
    container.className = 'toast-container position-fixed top-0 start-50 translate-middle-x p-3'
    container.style.zIndex = '1050'
    document.body.appendChild(container)
    return container
  }

  // 模擬 API 調用 - 未來可替換為真實 API
  private async simulateAPICall(data: { name: string; email: string; message: string }): Promise<void> {
    console.log('聯絡表單數據:', data)
    
    // 模擬 3-5 秒的延遲
    const delay = Math.random() * 2000 + 3000 // 3000-5000ms
    await new Promise(resolve => setTimeout(resolve, delay))
    
    // 未來這裡可以替換為真實的 API 調用
    // const response = await fetch('/api/contact', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(data)
    // })
    
    // if (!response.ok) {
    //   throw new Error('API call failed')
    // }
  }
}

// 關於我們元件
export class AboutComponent extends BaseComponent {
  constructor() {
    super({ container: 'about-container', template: '/components/about.html' })
  }

  bindEvents(): void {
    // 可以添加關於我們頁面的互動功能
    console.log('About section loaded')
  }
}
